import React from "react";
import { Printer } from "lucide-react";
import { cn } from "@/lib/utils";
import { resolveExportPrintPath } from "@/utils/exportShipmentPrintDocuments";
import { toast } from "sonner";

export const getShipmentFileRef = (shipment) =>
  shipment?.fileNumber || shipment?.FileRefNumber || shipment?.carrierBookingNumber || "—";

export const formatPortLabel = (name, code) => {
  const n = String(name || "").trim();
  const c = String(code || "").trim();
  if (n && c && !n.includes("[")) return `${n} [${c}]`;
  return n || c || "";
};

export const modalBodyClass =
  "relative z-0 -mt-3 min-h-0 flex-1 overflow-y-auto bg-slate-100/90 p-4 pt-5 sm:p-5 sm:pt-6 dark:bg-slate-950";

export const modalSectionClass =
  "rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:from-slate-900 dark:to-slate-900 dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200";

export function SectionCard({ title, description, children, className = "", headerRight = null }) {
  return (
    <div className={cn(modalSectionClass, className)}>
      <div className="mb-3 flex items-start justify-between gap-2 border-b border-slate-200/70 pb-2.5 dark:border-slate-700/70">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">{description}</p>
          ) : null}
        </div>
        {headerRight ? <div className="shrink-0">{headerRight}</div> : null}
      </div>
      {children}
    </div>
  );
}

export function FieldLabel({ children, hint, htmlFor, className = "" }) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn("block text-[12px] font-semibold text-slate-700 dark:text-slate-200", className)}
    >
      {children}
      {hint ? <span className="ml-1.5 font-normal text-slate-400">{hint}</span> : null}
    </label>
  );
}

export const inputClass =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-[13px] text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.28)] dark:focus:border-sky-500 dark:focus:bg-slate-800";

export const textareaClass =
  "mt-1.5 w-full min-h-[88px] resize-y rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-[13px] leading-relaxed text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.28)] dark:focus:border-sky-500 dark:focus:bg-slate-800";

export const selectClass = inputClass;

export const inlineInputClass =
  "inline-block min-w-[3.5rem] rounded-md border border-slate-300 bg-slate-50 px-2.5 py-1.5 text-[13px] text-slate-800 shadow-[inset_0_1px_2px_rgba(15,23,42,0.06)] outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.28)] dark:focus:border-sky-500";

export function FormField({ label, hint, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      {label ? <FieldLabel htmlFor={htmlFor} hint={hint}>{label}</FieldLabel> : null}
      {children}
    </div>
  );
}

export function FormRow({ label, children, className = "" }) {
  return (
    <div className={cn("grid grid-cols-1 gap-1 sm:grid-cols-[minmax(130px,32%)_1fr] sm:items-center sm:gap-3", className)}>
      <span className="text-[12px] font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <div>{children}</div>
    </div>
  );
}

export function ModalFooter({
  onSave,
  onClose,
  onPrint,
  saveLabel = "Save",
  saving = false,
  disabled = false,
}) {
  const busy = saving || disabled;
  return (
    <div className="flex items-center justify-end gap-2.5">
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="h-9 rounded-lg px-3.5 text-[13px] font-semibold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        Cancel
      </button>
      {onPrint ? (
        <button
          type="button"
          onClick={onPrint}
          disabled={busy}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 text-[13px] font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <Printer className="h-3.5 w-3.5" />
          Print
        </button>
      ) : null}
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        className="inline-flex h-9 items-center justify-center rounded-lg bg-emerald-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50"
      >
        {saving ? "Saving…" : saveLabel}
      </button>
    </div>
  );
}

export function openShipmentDocumentPrint(shipment, documentId) {
  const bookingId =
    shipment?.id ?? shipment?.BookingId ?? shipment?.BOOKING_ID ?? null;
  const fileNumber =
    shipment?.fileNumber || shipment?.FileRefNumber || shipment?.carrierBookingNumber || "";
  const path = resolveExportPrintPath(documentId, { bookingId, fileNumber });
  if (!path) {
    toast.error("Unable to open print view.");
    return;
  }
  window.open(path, "_blank", "noopener,noreferrer");
}

export function ModalToolbar({ children, className = "" }) {
  return (
    <div
      className={cn(
        "mb-4 flex flex-wrap items-center justify-end gap-2 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-2 shadow-sm hover:shadow-md dark:border-slate-700 dark:from-slate-800/50 dark:to-slate-900 dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function GradientActionButton({ className = "", children, ...props }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#0d9488] px-3 text-[12px] font-semibold text-white shadow-[0_2px_6px_rgba(37,99,235,0.35)] ring-1 ring-blue-500/20 transition-opacity hover:opacity-95",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

const toolbarBtnBase =
  "inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12px] font-semibold transition-colors disabled:opacity-50";

export function ToolbarButton({ variant = "outline", className = "", ...props }) {
  const variants = {
    outline:
      "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    primary:
      "bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100",
    soft:
      "bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-950/40 dark:text-sky-300 dark:hover:bg-sky-900/50",
  };
  return (
    <button type="button" className={cn(toolbarBtnBase, variants[variant], className)} {...props} />
  );
}

export function SignatureBlock({ label }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 shadow-sm hover:shadow-md dark:border-slate-600 dark:bg-slate-800 dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
      <div className="h-8 border-b border-slate-300 dark:border-slate-600" />
      <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
    </div>
  );
}

export function buildShipmentDefaults(shipment) {
  const pol = formatPortLabel(shipment?.portOfLoading, shipment?.portOfLoadingCode);
  const pod = formatPortLabel(shipment?.portOfDischarge, shipment?.portOfDischargeCode);
  const vessel = [shipment?.vessel, shipment?.voyage].filter(Boolean).join(" / ");
  return {
    blNo: shipment?.carrierBookingNumber || shipment?.hblNumber || "",
    portOfLoading: pol || "",
    portOfDischarge: pod || "",
    vesselName: vessel || "TO BE NOMINATED",
    customerOrderNo: shipment?.customerPoNo || shipment?.exportRefNumber || "",
    invoiceNumber: shipment?.invoiceNumber || "",
  };
}
